<template>
  <v-row>
    <v-col cols="12" md="6">
      <qtm-input-label :label="label" :required="required">
        <qtm-select
          v-bind="$attrs"
          v-model="selectedLocation"
          hide-details="auto"
          item-title="label"
          item-value="value"
          :items="items"
          :loading="loading"
          return-object
        >
          <template v-slot:item="{ item, props: itemProps }">
            <div v-if="item.raw.header" class="px-4 pt-1 my-2 text-caption font-weight-bold text-mid-dark-grey">
              <span class="bg-light-grey px-1 py-1 text-uppercase" v-text="item.raw.header" />
            </div>
            <v-list-item v-else v-bind="itemProps" title="">
              <v-list-item-title>
                {{ item.raw.label }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ item.raw.address }}, {{ item.raw.addressLine }}
              </v-list-item-subtitle>
            </v-list-item>
          </template>

          <template v-if="inventory" v-slot:no-data>
            <v-list-item>
              <v-list-item-title class="qtm-body text-mid-grey">
                To start restocking items, add at least 1 location in the Inventory.
              </v-list-item-title>
            </v-list-item>
          </template>
        </qtm-select>
      </qtm-input-label>
    </v-col>
    <v-col v-if="selectedLocation" cols="12" md="6">
      <qtm-input-label label="Address">
        <div v-text="selectedLocation.address" />
        <div v-text="selectedLocation.addressLine" />
      </qtm-input-label>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import type { Location, Jobsite } from '@quotetome/materials-api'

export interface Props {
  inventory?: boolean,
  jobsite?: Jobsite,
  label?: string,
  location?: number | null,
  locationType?: string
  noInventory?: boolean,
  required?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  label: "Delivery Address",
  jobsite: undefined,
  location: null,
  locationType: 'jobsite'
})

const emit = defineEmits(['address', 'update:inventory-location', 'update:location', 'update:location-type'])

const inventoryLocations = ref<any[]>([])
const loading = ref(false)
const office = ref()

const authStore = useAuthStore()
const userCompany = authStore.userCompany

const hasInventory = computed(() => {
  return office.value?.allowed_permissions?.some(perm => perm.codename === 'qtm_can_use_inventory')
})

const items = computed(() => {
  if (props.inventory) {
    return inventoryLocations.value.map(location => ({
      address: location.address,
      addressLine: buildAddressLine(location),
      label: location.name,
      location: location.id,
      locationType: 'office',
      value: location.id,
      inventory: true,
    }))
  }

  let items = [] as any[]
  let inventorySites = [] as any[]

  if (props.jobsite) {
    inventorySites = props.jobsite?.locations
      .filter(location => location.inventory)
      .map(location => ({
        address: location.address,
        addressLine: buildAddressLine(location),
        label: location.name,
        location: location.id,
        locationType: 'jobsite',
        value: `site-${location.id}`,
    }))

    if (!props.noInventory && hasInventory.value && inventorySites.length) {
      items.push({ header: 'Project Locations'})
    }

    items.push({
      address: props.jobsite.address,
      addressLine: buildAddressLine(props.jobsite),
      label: 'Site',
      location: null,
      locationType: 'jobsite',
      value: 'site',
      inventory: false,
    })

    if (props.jobsite?.locations?.length) {
    items = items.concat(props.jobsite?.locations
      .filter(location => !location.inventory)
      .map(location => ({
        address: location.address,
        addressLine: buildAddressLine(location),
        label: location.name,
        location: location.id,
        locationType: 'jobsite',
        value: `site-${location.id}`,
      })))
    }
  }

  if (office.value) {
    items.push({
      address: office.value.address,
      addressLine: buildAddressLine(office.value),
      label: 'Office',
      location: null,
      locationType: 'office',
      value: 'office',
      inventory: false,
    })
  }

  if (!props.noInventory && hasInventory.value && inventorySites.length) {
    items.push({ header: 'Inventory Locations'})
    items = items.concat(inventorySites)
  }

  return items
})

const selectedLocation = computed({
  get() {
    let location

    if (props.location) {
      location = items.value.find(item => item.location === props.location)
    }
    else if (props.locationType === 'jobsite') {
      location = items.value.find(item => item.value === 'site')
    }
    else if (props.locationType === 'office') {
      location = items.value.find(item => item.value === 'office')
    }

    if (location) {
      emit('address', `${location.address}, ${location.addressLine}`)
    }

    return location
  },

  set(item) {
    emit('update:location', item.location)
    emit('update:location-type', item.locationType)
  }
})

const buildAddressLine = (location: Location | Jobsite) => {
  return [location.city, location.province, location.postal_code].join(', ')
}

const { $api, $error } = useNuxtApp()

const fetchInventoryLocations = async () => {
  loading.value = true
  try {
    inventoryLocations.value = await $api.v1.locations.list({ archived: false, inventory: true })
      .then(response => response.data)
  }
  catch (error) {
    $error.report(error)
  }
  loading.value = false
}

const setOffice = async () => {
  office.value = undefined

  if (props.jobsite) {
    if (props.jobsite.company === userCompany?.id) {
      office.value = userCompany
      return
    }

    loading.value = true
    try {
      office.value = await $api.v1.companies.get(props.jobsite.company)
    }
    catch (error) {
      $error.report(error)
    }
    loading.value = false
  }
}

watch( () => props.jobsite, setOffice, { immediate: true })
watch( () => selectedLocation.value, () => {
  emit('update:inventory-location', selectedLocation.value?.inventory ?? false)
  },
  { immediate: true }
)
watch( () => props.inventory, () => {
  if (props.inventory) {
      fetchInventoryLocations()
    }
  },
  { immediate: true }
)
</script>
